import type React from 'react';
import {type ReactElement} from 'react';
import {useFormikContext} from 'formik';

import UITextField from '@/components/ui/TextField';

type TextFieldProps = React.InputHTMLAttributes<HTMLInputElement> & {
  name: string;
  type?: 'number' | 'password' | 'textarea' | 'text';
  label: string;
  testId?: string;
};

const TextField = ({
  name,
  type = 'text',
  label,
  id,
  testId,
  ...props
}: TextFieldProps): ReactElement => {
  const {getFieldProps, getFieldMeta} = useFormikContext();
  const {onChange, onBlur} = getFieldProps(name);
  const {value, error, touched} = getFieldMeta(name);
  const hasError = touched && Boolean(error);
  const inputId = id ?? name;
  const errorId = `${inputId}-error`;

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={inputId} className="text-sm font-bold text-peppercorn-500">
        {label}
      </label>
      <UITextField.Root
        className="px-3 py-1.5"
        error={hasError}
        suppressErrorIcon={type === 'textarea'}
      >
        {type === 'textarea' ? (
          <>
            <UITextField.Textarea
              {...(props as unknown as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
              aria-invalid={hasError}
              aria-describedby={hasError ? errorId : undefined}
              data-testid={testId}
              id={inputId}
              name={name}
              value={value as string}
              onChange={onChange}
              onBlur={onBlur}
            />
            {props.maxLength != null && (
              <UITextField.Slot className="ml-auto px-3 pb-2 text-sm text-peppercorn-500">
                {(value as string).length}/{props.maxLength}
              </UITextField.Slot>
            )}
          </>
        ) : (
          <UITextField.Input
            {...props}
            aria-invalid={hasError}
            aria-describedby={hasError ? errorId : undefined}
            className="p-0"
            data-testid={testId}
            id={inputId}
            name={name}
            type={type}
            value={value as string}
            onChange={onChange}
            onBlur={onBlur}
          />
        )}
      </UITextField.Root>
      {hasError && (
        <div id={errorId} className="text-sm text-guava-400" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default TextField;
